import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FaUsers, FaChevronDown, FaTimes, FaSpinner, FaHandPaper } from 'react-icons/fa';
import API from '../../API';
import intersectStaffIcon from '../images/intersect-staff-blue-icon.png';
import UnityGameDockApp from './UnityGameDockApp';

/** Converts snake_case or slug-style strings to Title Case for display (e.g. "associate_member" → "Associate Member"). */
function formatLabel(value: string | undefined | null): string {
  if (value == null || value === '') return '';
  return value
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export interface RoomParticipant {
  id: string;
  userId: string;
  publishedTracks: any[];
  subscribedTracks: any[];
  isIntersectStaff?: boolean;
  intersectId?: string;
}

export interface IntersectUserProfile {
  username: string;
  intersect_staff: boolean;
  membership_type: string;
  membership_expiry?: string | null;
  committees: Array<{
    properties: {
      name: string;
      role: { name: string; slug: string };
    };
  }>;
}

/** Presence row from GET /events/:eventId/rooms/:roomId/presence */
export interface RoomPresenceUser {
  intersect_user_id: string;
  username: string;
  game_server_instance_id?: string;
  last_seen: number;
}

/** Match server `roomPresenceTTL` (120s): hide rows older than this even if DDB has not evicted yet. */
const PRESENCE_MAX_AGE_MS = 120_000;
/** Poll while closed; faster refresh when the menu is open so the list feels live. */
const PRESENCE_POLL_MS_CLOSED = 5000;
const PRESENCE_POLL_MS_OPEN = 2500;

/** IVS-driven raised hands; same shape as parent `handRaiseByUserId` entries + userId. */
export interface RaisedHandListItem {
  userId: string;
  displayName: string;
  raisedAt: number;
}

interface RoomUsersWidgetProps {
  eventId: string;
  roomId: string;
  accessToken?: string;
  currentUserId?: string;
  dockMode?: boolean;
  /** Raised-hand queue (Meet-style); shown at top of People menu. */
  raisedHands?: RaisedHandListItem[];
  /** Intersect staff: show control to lower someone’s hand for the whole room (IVS broadcast). */
  isIntersectStaff?: boolean;
  onModeratorUnraiseHand?: (userId: string) => void | Promise<void>;
}

const RoomUsersWidget: React.FC<RoomUsersWidgetProps> = ({
  eventId,
  roomId,
  accessToken,
  currentUserId,
  dockMode = true,
  raisedHands = [],
  isIntersectStaff = false,
  onModeratorUnraiseHand,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [unraisingUserId, setUnraisingUserId] = useState<string | null>(null);
  const [presenceUsers, setPresenceUsers] = useState<RoomPresenceUser[]>([]);
  const [presenceLoading, setPresenceLoading] = useState(true);
  const [profileUserId, setProfileUserId] = useState<string | null>(null);
  const [profile, setProfile] = useState<IntersectUserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const presenceRoomKeyRef = useRef<string | null>(null);
  /** Stable id for portaled dock popover (React 17 — no React 18 id hook) */
  const popoverOwnerId = useMemo(() => `dock-popover-${Math.random().toString(36).slice(2)}`, []);

  const participants: RoomParticipant[] = useMemo(
    () =>
      presenceUsers.map((u) => ({
        id: u.intersect_user_id,
        userId: u.username,
        intersectId: u.intersect_user_id,
        publishedTracks: [] as any[],
        subscribedTracks: [] as any[],
      })),
    [presenceUsers]
  );

  const isLocalParticipant = (p: RoomParticipant) =>
    Boolean(currentUserId && (p.intersectId === currentUserId || p.userId === currentUserId));

  const displayName = (p: RoomParticipant) =>
    p.userId || p.intersectId || p.id || `User ${(p.id || '').slice(0, 8)}`;

  const raisedHandsOrdered = useMemo(() => {
    return [...raisedHands].sort((a, b) => a.raisedAt - b.raisedAt);
  }, [raisedHands]);

  const hasRaisedHands = raisedHandsOrdered.length > 0;

  const peopleAriaLabel =
    hasRaisedHands && raisedHandsOrdered.length === 1
      ? 'People in this room, 1 person raising hand'
      : hasRaisedHands
        ? `People in this room, ${raisedHandsOrdered.length} people raising hand`
        : 'People in this room';

  const raisedHandsDockBadge =
    hasRaisedHands ? (
      <>
        <FaHandPaper className="h-3 w-3 shrink-0 drop-shadow-sm" aria-hidden />
        {raisedHandsOrdered.length > 1 ? (
          <span className="tabular-nums leading-none">{raisedHandsOrdered.length}</span>
        ) : null}
      </>
    ) : undefined;

  useEffect(() => {
    if (!accessToken || !eventId || !roomId) {
      setPresenceUsers([]);
      setPresenceLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const res = await API.getRoomPresence(accessToken, { eventId, roomId });
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          const raw = (data.users || []) as RoomPresenceUser[];
          const now = Date.now();
          const users = raw.filter((u) => now - u.last_seen < PRESENCE_MAX_AGE_MS);
          setPresenceUsers(users);
        } else {
          setPresenceUsers([]);
        }
      } catch {
        if (!cancelled) setPresenceUsers([]);
      } finally {
        if (!cancelled) setPresenceLoading(false);
      }
    };

    const roomKey = `${accessToken}:${eventId}:${roomId}`;
    if (presenceRoomKeyRef.current !== roomKey) {
      presenceRoomKeyRef.current = roomKey;
      setPresenceLoading(true);
    }
    load();
    const pollMs = isOpen ? PRESENCE_POLL_MS_OPEN : PRESENCE_POLL_MS_CLOSED;
    const t = window.setInterval(load, pollMs);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [accessToken, eventId, roomId, isOpen]);

  const openProfile = async (p: RoomParticipant) => {
    const idToFetch = p.intersectId || p.userId;
    if (!idToFetch || !accessToken) {
      setProfileError('Cannot load profile');
      setProfile(null);
      setProfileUserId(idToFetch || null);
      return;
    }
    setProfileUserId(idToFetch);
    setProfile(null);
    setProfileError(null);
    setProfileLoading(true);
    setIsOpen(false);
    try {
      const response = await API.getIntersectUserProfile(accessToken, { userId: idToFetch });
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      } else {
        setProfileError('Profile unavailable');
      }
    } catch {
      setProfileError('Failed to load profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const closeProfileModal = () => {
    setProfileUserId(null);
    setProfile(null);
    setProfileError(null);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const t = event.target as Node;
      if (dropdownRef.current?.contains(t)) return;
      if (
        dockMode &&
        (t as Element).closest?.(`[data-unity-dock-popover-owner="${popoverOwnerId}"]`)
      ) {
        return;
      }
      if ((t as Element).closest?.('[data-room-users-profile-modal]')) return;
      setIsOpen(false);
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, dockMode, popoverOwnerId]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (profileUserId !== null) closeProfileModal();
        else setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [profileUserId]);

  const raisedHandsSection = hasRaisedHands ? (
      <>
        <div className="border-b border-amber-600/40 bg-amber-950/40 px-3 py-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-amber-400/95">
            Raised hands
          </div>
        </div>
        <div className="max-h-40 overflow-y-auto border-b border-gray-700 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-gray-600 [&::-webkit-scrollbar]:w-1">
          {raisedHandsOrdered.map((item, index) => (
            <div
              key={item.userId}
              className="flex items-center gap-1 border-b border-gray-800/80 px-2 py-2 last:border-b-0 sm:gap-2 sm:px-3 sm:py-2.5"
            >
              <span
                className="unity-people-raised-hand-icon shrink-0 text-amber-500"
                aria-hidden
              >
                <FaHandPaper className="h-4 w-4" />
              </span>
              <span className="w-6 shrink-0 font-mono text-[10px] tabular-nums text-gray-500 sm:w-7 sm:text-xs">
                #{index + 1}
              </span>
              <span className="min-w-0 flex-1 truncate text-xs font-medium text-amber-100 sm:text-sm">
                {item.displayName}
              </span>
              {isIntersectStaff && onModeratorUnraiseHand && (
                <button
                  type="button"
                  disabled={unraisingUserId === item.userId}
                  className="shrink-0 rounded-md bg-amber-700/50 px-1.5 py-1 text-[10px] font-semibold text-amber-100 transition-colors hover:bg-amber-600/60 disabled:cursor-not-allowed disabled:opacity-50 sm:px-2 sm:text-xs"
                  title="Lower their hand for everyone in the room"
                  onClick={async (e) => {
                    e.stopPropagation();
                    setUnraisingUserId(item.userId);
                    try {
                      await onModeratorUnraiseHand(item.userId);
                    } finally {
                      setUnraisingUserId(null);
                    }
                  }}
                >
                  {unraisingUserId === item.userId ? '…' : 'Lower'}
                </button>
              )}
            </div>
          ))}
        </div>
      </>
    ) : null;

  const listSection = (
    <>
      {raisedHandsSection}
      <div className="border-b border-gray-700 px-3 py-2 text-xs uppercase tracking-wide text-gray-400">
        In this room
      </div>
      <div className="max-h-64 flex-1 overflow-y-auto [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-gray-600 [&::-webkit-scrollbar]:w-1">
        {presenceLoading && participants.length === 0 ? (
          <div className="px-4 py-3 text-sm text-gray-500">Loading…</div>
        ) : participants.length === 0 ? (
          <div className="px-4 py-3 text-sm text-gray-500">No one here yet</div>
        ) : (
          participants.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => openProfile(p)}
              className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-white transition-colors hover:bg-gray-800"
            >
              <span className="min-w-0 flex-1 truncate text-white">
                {displayName(p)}
                {isLocalParticipant(p) && <span className="ml-1 font-normal text-gray-400">(you)</span>}
              </span>
            </button>
          ))
        )}
      </div>
    </>
  );

  return (
    <div ref={dropdownRef} className="relative">
      {dockMode ? (
        <UnityGameDockApp
          popoverOwnerId={popoverOwnerId}
          dataTourId="tour-people-button"
          ariaLabel={peopleAriaLabel}
          label="People"
          icon={<FaUsers />}
          active={isOpen}
          badge={participants.length > 0 ? participants.length : undefined}
          secondaryBadge={raisedHandsDockBadge}
          wrapperClassName={hasRaisedHands ? 'unity-dock-people--hands-active' : undefined}
          tileClassName={
            hasRaisedHands
              ? 'bg-gradient-to-br from-amber-600 via-violet-700 to-purple-900'
              : 'bg-gradient-to-br from-violet-600 to-purple-900'
          }
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
        >
          {isOpen && (
            <div
              className="unity-dock-popover unity-dock-popover--enter flex min-w-[200px] flex-col overflow-hidden border-gray-700 bg-gray-900"
              onClick={(e) => e.stopPropagation()}
            >
              {listSection}
            </div>
          )}
        </UnityGameDockApp>
      ) : (
        <>
          <button
            type="button"
            data-tour-id="tour-people-button"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
            className={`relative flex items-center gap-2 rounded p-2 text-white shadow-lg transition-all duration-200 hover:bg-opacity-100 ${
              hasRaisedHands
                ? `bg-gradient-to-br from-amber-600 to-violet-700 ${isOpen ? 'bg-opacity-100' : 'bg-opacity-90'} unity-dock-people--hands-active`
                : `bg-violet-600 ${isOpen ? 'bg-opacity-100' : 'bg-opacity-90'}`
            }`}
            style={{ zIndex: 10000 }}
            title={peopleAriaLabel}
            aria-label={peopleAriaLabel}
          >
            <span className="relative inline-flex shrink-0">
              <FaUsers size={16} />
              {hasRaisedHands && (
                <span
                  className="absolute -bottom-1 -left-1 flex min-h-[1rem] min-w-[1rem] items-center justify-center gap-px rounded-full bg-amber-500 px-0.5 text-[8px] font-bold text-white shadow ring-1 ring-amber-950/30"
                  aria-hidden
                >
                  <FaHandPaper className="h-2.5 w-2.5" />
                  {raisedHandsOrdered.length > 1 ? (
                    <span className="tabular-nums leading-none">{raisedHandsOrdered.length}</span>
                  ) : null}
                </span>
              )}
            </span>
            <span className="text-sm font-medium">People</span>
            {participants.length > 0 && (
              <span className="rounded bg-white/20 px-1.5 py-0.5 text-xs">{participants.length}</span>
            )}
            <FaChevronDown size={12} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
          </button>
          {isOpen && (
            <div
              className="animate-fadeIn absolute right-0 top-full z-[10001] mt-2 flex max-h-[320px] min-w-[200px] flex-col overflow-hidden rounded-lg border border-gray-700 bg-gray-900 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              {listSection}
            </div>
          )}
        </>
      )}

      {/* Profile modal — portaled so fixed positioning is not clipped by dock transform */}
      {profileUserId !== null &&
        (profileLoading || profile || profileError) &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            data-room-users-profile-modal
            className="fixed inset-0 z-[10003] flex items-center justify-center bg-black/80"
            style={{ pointerEvents: 'auto' }}
            onClick={closeProfileModal}
          >
            <div
              className="bg-[#1e1e1e] rounded-xl shadow-2xl border border-[#333] w-[90%] max-w-[400px] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-[#333]">
                <h2 className="text-white text-lg font-semibold">Profile</h2>
                <button
                  onClick={closeProfileModal}
                  className="p-2 text-gray-400 hover:text-white hover:bg-[#333] rounded-lg transition-colors"
                >
                  <FaTimes size={18} />
                </button>
              </div>
              <div className="p-4">
                {profileLoading && (
                  <div className="flex flex-col items-center justify-center py-8 gap-2 text-gray-400">
                    <FaSpinner className="animate-spin" size={28} />
                    <span className="text-sm">Loading...</span>
                  </div>
                )}
                {profileError && !profileLoading && (
                  <div className="py-6 text-center text-gray-400 text-sm">{profileError}</div>
                )}
                {profile && !profileLoading && (
                  <div className="space-y-4 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-white font-bold text-lg tracking-tight">
                        {profile.username}
                      </span>
                      {profile.intersect_staff && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-500/20 text-amber-400 border border-amber-500/40 text-xs font-semibold">
                          <img src={intersectStaffIcon} alt="" className="w-3.5 h-3.5" aria-hidden />
                          Staff
                        </span>
                      )}
                    </div>
                    <div>
                      <span className="text-gray-500 block text-xs uppercase tracking-wide mb-0.5">Membership type</span>
                      <span className="text-white">{formatLabel(profile.membership_type) || '—'}</span>
                    </div>
                    {profile.membership_expiry && (
                      <div>
                        <span className="text-gray-500 block text-xs uppercase tracking-wide mb-0.5">Membership expiry</span>
                        <span className="text-white">{profile.membership_expiry}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500 block text-xs uppercase tracking-wide mb-1">Committees</span>
                      {profile.committees && profile.committees.length > 0 ? (
                        <ul className="space-y-0.5 text-white">
                          {profile.committees.map((c, i) => (
                            <li key={i}>
                              {formatLabel(c.properties?.name) || 'Committee'}
                              {(c.properties?.role?.name || c.properties?.role?.slug) && (
                                <span className="text-gray-400 ml-1">
                                  ({formatLabel(c.properties?.role?.name || c.properties?.role?.slug)})
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-gray-400">None</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.15s ease-out; }
      `}</style>
    </div>
  );
};

export default RoomUsersWidget;
